import { Copy, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { CalendarPost, CalendarResponse, Product, ProductsResponse } from '@/types'
import { ImageUploadField } from '@/components/ImageUploadField'
import { EmptyState, Spinner } from '@/components/ui'
import { RequestError, api } from '@/lib/api'
import { AdminSection } from './AdminSection'

const MAX_COPY = 500

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
const fromLocalInput = (local: string): string => (local ? new Date(local).toISOString() : '')
const fmt = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-AR', { dateStyle: 'medium', timeStyle: 'short' })
}

interface Draft {
  id?: string
  scheduledAt: string // datetime-local
  story: string
  post: string
  thumb: string
  copy: string
  productId?: string
  productName?: string
  productUrl?: string
}

function ProductPicker({ draft, onPick, onClear }: { draft: Draft; onPick: (p: Product) => void; onClear: () => void }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Product[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    const t = setTimeout(async () => {
      const term = q.trim()
      if (term.length < 2) return setResults([])
      setSearching(true)
      try {
        const res = await api.get<ProductsResponse>(`/products?filter=all&search=${encodeURIComponent(term)}`)
        setResults(res.products.slice(0, 5))
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => clearTimeout(t)
  }, [q])

  if (draft.productId) {
    return (
      <div className="flex items-center justify-between rounded-xl border border-line bg-surface-sunken px-3 py-2 text-sm">
        <span className="truncate text-ink">🔗 {draft.productName}</span>
        <button className="text-xs text-danger" onClick={onClear}>Quitar</button>
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3">
        <Search className="h-4 w-4 text-ink-faint" />
        <input className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-ink-faint" placeholder="Buscar producto por nombre…" value={q} onChange={(e) => setQ(e.target.value)} />
        {searching && <Spinner className="h-4 w-4" />}
      </div>
      {results.length > 0 && (
        <div className="mt-1 space-y-1">
          {results.map((p) => (
            <button key={p.skuId} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-surface-sunken" onClick={() => { onPick(p); setQ(''); setResults([]) }}>
              {p.imageUrl && <img src={p.imageUrl} alt="" className="h-8 w-8 rounded object-cover" />}
              <span className="truncate">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PostForm({ initial, onClose, onSaved }: { initial: Draft; onClose: () => void; onSaved: (post: CalendarPost, id?: string) => void }) {
  const isEdit = !!initial.id
  const [d, setD] = useState<Draft>(initial)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    const body = {
      scheduledAt: fromLocalInput(d.scheduledAt),
      images: { story: d.story, post: d.post, thumb: d.thumb },
      copy: d.copy,
      productId: d.productId,
      productName: d.productName,
      productUrl: d.productUrl,
    }
    try {
      if (isEdit) {
        await api.put(`/calendar/${d.id}`, body)
        onSaved({ id: d.id!, ...body }, d.id)
      } else {
        const created = await api.post<CalendarPost>('/calendar', body)
        onSaved(created)
      }
    } catch (e2) {
      setErr(e2 instanceof RequestError ? e2.message : 'No se pudo guardar.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet no-scrollbar overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="flex items-center justify-between px-4 py-2">
          <h3 className="font-bold text-ink">{isEdit ? 'Editar publicación' : 'Nueva publicación'}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-ink-muted hover:bg-surface-sunken"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3 px-4 pb-8">
          <div>
            <label className="field-label">Fecha y hora *</label>
            <input type="datetime-local" required className="field-input" value={d.scheduledAt} onChange={(e) => setD({ ...d, scheduledAt: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Producto vinculado (opcional)</label>
            <ProductPicker draft={d} onPick={(p) => setD({ ...d, productId: p.productId, productName: p.name, productUrl: p.productUrl })} onClear={() => setD({ ...d, productId: undefined, productName: undefined, productUrl: undefined })} />
          </div>
          <div>
            <label className="field-label">Copy sugerido ({d.copy.length}/{MAX_COPY})</label>
            <textarea className="field-input min-h-[80px] resize-none" maxLength={MAX_COPY} value={d.copy} onChange={(e) => setD({ ...d, copy: e.target.value })} />
          </div>
          <div className="space-y-3">
            <ImageUploadField label="Story (1080×1920)" value={d.story} onChange={(url) => setD({ ...d, story: url })} placeholder="Story…" />
            <ImageUploadField label="Post (1080×1080)" value={d.post} onChange={(url) => setD({ ...d, post: url })} placeholder="Post…" />
            <ImageUploadField label="Miniatura WhatsApp" value={d.thumb} onChange={(url) => setD({ ...d, thumb: url })} placeholder="Miniatura…" />
          </div>
          {err && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}
          <button className="btn-primary btn-block" disabled={busy}>{busy ? <Spinner className="h-4 w-4" /> : 'Guardar'}</button>
        </form>
      </div>
    </div>
  )
}

const EMPTY_DRAFT: Draft = { scheduledAt: '', story: '', post: '', thumb: '', copy: '' }
const postToDraft = (p: CalendarPost): Draft => ({
  id: p.id, scheduledAt: toLocalInput(p.scheduledAt), story: p.images.story ?? '', post: p.images.post ?? '',
  thumb: p.images.thumb ?? '', copy: p.copy, productId: p.productId, productName: p.productName, productUrl: p.productUrl,
})

export function AdminCalendar() {
  const [posts, setPosts] = useState<CalendarPost[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [form, setForm] = useState<Draft | null>(null)

  async function load() {
    setErr(null)
    try {
      const res = await api.get<CalendarResponse>('/calendar')
      setPosts([...res.posts].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)))
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  useEffect(() => {
    void load()
  }, [])

  function handleSaved(post: CalendarPost, editedId?: string) {
    setForm(null)
    setPosts((list) => {
      const cur = list ?? []
      const next = editedId ? cur.map((p) => (p.id === editedId ? post : p)) : [...cur, post]
      return next.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    })
  }

  async function remove(p: CalendarPost) {
    const snapshot = posts
    setPosts((list) => list?.filter((x) => x.id !== p.id) ?? null)
    try {
      await api.del(`/calendar/${p.id}`)
    } catch (e) {
      setPosts(snapshot ?? null)
      setErr((e as Error).message)
    }
  }

  async function duplicate(p: CalendarPost) {
    try {
      const created = await api.post<CalendarPost>('/calendar', { ...p, id: undefined })
      setPosts((list) => [...(list ?? []), created].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)))
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  return (
    <AdminSection title="Calendario de contenido">
      <button className="btn-primary btn-block mb-3" onClick={() => setForm({ ...EMPTY_DRAFT })}>
        <Plus className="h-4 w-4" /> Crear publicación
      </button>
      {err && <div className="mb-3 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}

      {posts === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6 accent-text" /></div>
      ) : posts.length === 0 ? (
        <EmptyState title="Sin publicaciones" subtitle="Creá la primera publicación para el equipo." />
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <div key={p.id} className="card p-3">
              <div className="flex items-start gap-3">
                {p.images.post || p.images.story ? (
                  <img src={p.images.post || p.images.story} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded-lg bg-surface-sunken" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold accent-text">{fmt(p.scheduledAt)}</p>
                  {p.productName && <p className="truncate text-xs text-ink-muted">🔗 {p.productName}</p>}
                  <p className="line-clamp-2 text-sm text-ink">{p.copy || <span className="text-ink-faint">(sin copy)</span>}</p>
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-1">
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => duplicate(p)}><Copy className="h-3.5 w-3.5" /> Duplicar</button>
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setForm(postToDraft(p))}><Pencil className="h-3.5 w-3.5" /> Editar</button>
                <button className="btn-ghost px-2 py-1 text-xs text-danger" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5" /> Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {form && <PostForm initial={form} onClose={() => setForm(null)} onSaved={handleSaved} />}
    </AdminSection>
  )
}
